import React, { useState, useEffect } from 'react';
import { auditsApi } from '../services/api';
import { User } from '../contexts/AuthContext';

interface SupervisorAuditReviewProps {
  user: User;
}

interface Audit {
  id: number;
  titulo: string;
  descripcion: string;
  estado: string;
  clienteNombre: string;
  operadorNombre: string;
  fechaTerminacion: string;
  observacionesOperador: string;
  observacionesSupervisor: string;
  archivoWord: string;
  costoFinal: number;
  tiempoRealMinutos: number;
}

const SupervisorAuditReview: React.FC<SupervisorAuditReviewProps> = ({ user }) => {
  const [audits, setAudits] = useState<Audit[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedAudit, setSelectedAudit] = useState<Audit | null>(null);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [comentariosSupervisor, setComentariosSupervisor] = useState('');
  const [processingApproval, setProcessingApproval] = useState(false);
  const [customTitle, setCustomTitle] = useState('');
  // Variable removida: Ya no necesitamos manejar archivos de corrección

  useEffect(() => {
    loadAuditsForReview();
  }, []);

  const loadAuditsForReview = async () => {
    try {
      setLoading(true);
      const response = await auditsApi.getPendingReview();
      if (response.data.success) {
        setAudits(response.data.auditorias);
      }
    } catch (error) {
      console.error('Error cargando auditorías para revisión:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleReviewAudit = (audit: Audit) => {
    setSelectedAudit(audit);
    setComentariosSupervisor('');
    setCustomTitle(audit.titulo); // Inicializar con el título actual
    setShowReviewModal(true);
  };

  // Función removida: Ya no necesitamos descargar Word

  const handleDownloadHtml = async (auditId: number) => {
    try {
      // Construir URL completa con detección automática de IP
      const getApiBaseUrl = () => {
        if (typeof window !== 'undefined') {
          const hostname = window.location.hostname;
          if (hostname === 'localhost' || hostname === '127.0.0.1') {
            return 'http://localhost:4000';
          }
          return `${window.location.protocol}//${window.location.host}`;
        }
        return 'http://localhost:4000';
      };
      const url = `${getApiBaseUrl()}/api/audits/${auditId}/report/html`;
      
      // Abrir HTML en nueva ventana con token de autenticación
      const token = localStorage.getItem('token');
      if (!token) {
        alert('No estás autenticado. Por favor, inicia sesión nuevamente.');
        return;
      }

      // Usar fetch para obtener el HTML y abrirlo en nueva ventana
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const htmlContent = await response.text();
        const newWindow = window.open('', '_blank', 'width=1200,height=800,scrollbars=yes');
        if (newWindow) {
          newWindow.document.write(htmlContent);
          newWindow.document.close();
        }
      } else {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }
    } catch (error) {
      console.error('Error abriendo reporte HTML:', error);
      alert('Error al abrir el reporte HTML');
    }
  };

  const handleEditForm = async (audit: Audit) => {
    try {
      // Crear parámetros para abrir el formulario en modo supervisor
      const supervisorParams = new URLSearchParams({
        auditId: audit.id.toString(),
        mode: 'supervisor',
        operatorId: audit.operadorId?.toString() || '',
        returnTo: 'supervisor-review'
      });
      
      // Abrir el mismo formulario del operador en nueva ventana/pestaña
      const url = `/audit-template?${supervisorParams.toString()}`;
      window.open(url, '_blank', 'width=1400,height=900,scrollbars=yes,resizable=yes');
      
      // Cerrar la modal actual
      setShowReviewModal(false);
    } catch (error) {
      console.error('Error abriendo formulario editable:', error);
      alert('Error al abrir el formulario editable');
    }
  };

  // Función removida: Ya no necesitamos manejar archivos Word

  const handleApproveAudit = async (aprobada: boolean) => {
    if (!selectedAudit) {
      console.error('[handleApproveAudit] No hay auditoría seleccionada');
      return;
    }

    // console.log('[handleApproveAudit] Procesando:', {
    //   auditId: selectedAudit.id,
    //   estado: selectedAudit.estado,
    //   comentarios: comentariosSupervisor,
    //   aprobada
    // });

    try {
      setProcessingApproval(true);
      
      if (aprobada) {
        // Aprobar auditoría con cambios del supervisor
        const response = await auditsApi.supervisorApprove(selectedAudit.id, {
          observacionesSupervisor: comentariosSupervisor,
          observacionesFinales: comentariosSupervisor,
          eventosEditados: [], // Sin cambios de eventos desde este modal
          wordApproved: true,
          titulo: customTitle // Enviar el título personalizado
        });


        if (response.data.success) {
          alert('✅ Auditoría aprobada correctamente');
          // Recargar la lista FORZADAMENTE
          await loadAuditsForReview();
          // Cerrar modal DESPUÉS de recargar
          setShowReviewModal(false);
          setSelectedAudit(null);
          setComentariosSupervisor('');
        } else {
          alert('❌ Error: ' + (response.data.message || 'No se pudo aprobar'));
        }
      } else {
        // Validar que haya comentarios para solicitar corrección
        if (!comentariosSupervisor || comentariosSupervisor.trim() === '') {
          alert('❌ Debe escribir un comentario explicando qué necesita corregir el operador');
          setProcessingApproval(false);
          return;
        }
        
        // Solicitar corrección - DEVOLVER AL OPERADOR
        const response = await auditsApi.devolverOperador(selectedAudit.id, comentariosSupervisor.trim());


        if (response.data.success) {
          alert(`✅ ${response.data.message || 'Auditoría devuelta para corrección'}`);
          // Recargar la lista de auditorías
          await loadAuditsForReview();
          setShowReviewModal(false);
          setSelectedAudit(null);
          setComentariosSupervisor('');
        } else {
          console.error('[handleApproveAudit] Respuesta no exitosa:', response.data);
          alert('Error: ' + (response.data.message || 'Respuesta del servidor no exitosa'));
        }
      }
    } catch (error: any) {
      console.error('[handleApproveAudit] Error completo:', error);
      console.error('[handleApproveAudit] Error response:', error.response?.data);
      console.error('[handleApproveAudit] Error status:', error.response?.status);
      alert('Error al procesar la aprobación: ' + (error.response?.data?.error || error.message));
    } finally {
      setProcessingApproval(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const formatTime = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  const getPriorityColor = (prioridad: string) => {
    switch (prioridad) {
      case 'urgente': return '#ef4444';
      case 'prioritaria': return '#f59e0b';
      case 'normal': return '#10b981';
      default: return '#6b7280';
    }
  };

  const getStateColor = (estado: string) => {
    switch (estado) {
      case 'terminada': return '#f59e0b';
      case 'entregada': return '#10b981';
      default: return '#6b7280';
    }
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '200px' 
      }}>
        <div className="spinner" />
        <span style={{ marginLeft: '0.5rem' }}>Cargando auditorías...</span>
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: '2rem' }}>
        <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '0.5rem' }}>
          📋 Revisión de Auditorías
        </h2>
        <p style={{ color: '#6b7280' }}>
          Auditorías completadas por operadores que requieren revisión y aprobación.
        </p>
      </div>

      {audits.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: '3rem' }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>✅</div>
          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.5rem' }}>
            No hay auditorías pendientes de revisión
          </h3>
          <p style={{ color: '#6b7280' }}>
            Todas las auditorías han sido revisadas y aprobadas.
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          {audits.map((audit) => (
            <div key={audit.id} className="card">
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'flex-start',
                marginBottom: '1rem'
              }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.5rem' }}>
                    <h3 style={{ fontSize: '1.25rem', fontWeight: '600', margin: 0 }}>
                      {audit.titulo}
                    </h3>
                    <span style={{
                      backgroundColor: getPriorityColor(audit.prioridad),
                      color: 'white',
                      padding: '0.25rem 0.75rem',
                      borderRadius: '9999px',
                      fontSize: '0.75rem',
                      fontWeight: '600',
                      textTransform: 'uppercase'
                    }}>
                      {audit.prioridad}
                    </span>
                    <span style={{
                      backgroundColor: getStateColor(audit.estado),
                      color: 'white',
                      padding: '0.25rem 0.75rem',
                      borderRadius: '9999px',
                      fontSize: '0.75rem',
                      fontWeight: '600'
                    }}>
                      {audit.estado}
                    </span>
                  </div>
                  
                  <p style={{ color: '#6b7280', marginBottom: '1rem' }}>
                    {audit.descripcion}
                  </p>

                  <div style={{ 
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                    gap: '1rem',
                    fontSize: '0.875rem'
                  }}>
                    <div>
                      <strong>Cliente:</strong> {audit.clienteNombre}
                    </div>
                    <div>
                      <strong>Sucursal:</strong> {audit.descripcion || 'Sin descripción'}
                    </div>
                    <div>
                      <strong>Operador:</strong> {audit.operadorNombre}
                    </div>
                    <div>
                      <strong>Tiempo trabajado:</strong> {formatTime(audit.tiempoTrabajado)}
                    </div>
                    <div>
                      <strong>Finalizada:</strong> {formatDate(audit.fechaTerminacion)}
                    </div>
                    <div>
                      <strong>Eventos:</strong> {audit.eventos?.length || 0}
                    </div>
                  </div>

                  {audit.observacionesOperador && (
                    <div style={{ 
                      marginTop: '1rem',
                      padding: '1rem',
                      backgroundColor: '#f9fafb',
                      borderRadius: '8px',
                      border: '1px solid #e5e7eb'
                    }}>
                      <strong style={{ display: 'block', marginBottom: '0.5rem' }}>
                        👷 Observaciones del Operador:
                      </strong>
                      <p style={{ margin: 0, fontSize: '0.875rem' }}>
                        {audit.observacionesOperador}
                      </p>
                    </div>
                  )}

                  {audit.observacionesSupervisor && (
                    <div style={{ 
                      marginTop: '1rem',
                      padding: '1rem',
                      backgroundColor: '#fff3cd',
                      borderRadius: '8px',
                      border: '1px solid #ffeeba'
                    }}>
                      <strong style={{ display: 'block', marginBottom: '0.5rem' }}>
                        👨‍💼 Mis mensajes como Supervisor:
                      </strong>
                      <div style={{ fontSize: '0.875rem' }}>
                        {audit.observacionesSupervisor.split('\n').map((linea, idx) => (
                          <div key={idx} style={{ marginTop: idx > 0 ? '0.5rem' : 0 }}>
                            {linea}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div style={{ marginLeft: '1rem' }}>
                  <button
                    onClick={() => handleReviewAudit(audit)}
                    className="btn btn-primary"
                    style={{ fontSize: '0.875rem' }}
                  >
                    📝 Revisar y Aprobar
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal de Revisión */}
      {showReviewModal && selectedAudit && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            maxWidth: '600px',
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <div style={{
              background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
              color: 'white',
              padding: '20px',
              borderRadius: '12px 12px 0 0'
            }}>
              <h2 style={{ margin: 0, fontSize: '1.5rem' }}>
                📝 Revisión de Auditoría
              </h2>
              <p style={{ margin: '0.5rem 0 0 0', opacity: 0.9 }}>
                {selectedAudit.titulo} - {selectedAudit.clienteNombre}
              </p>
            </div>

            <div style={{ padding: '25px' }}>
              <div style={{ marginBottom: '20px' }}>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  marginBottom: '1rem'
                }}>
                  <h3 style={{ fontSize: '1.125rem', fontWeight: '600', margin: 0 }}>
                    Detalles de la Auditoría
                  </h3>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <button
                      onClick={() => handleDownloadHtml(selectedAudit.id)}
                      style={{
                        background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                        color: 'white',
                        border: 'none',
                        padding: '8px 16px',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '0.875rem',
                        fontWeight: '500'
                      }}
                    >
                      🌐 Ver Reporte HTML
                    </button>
                    <button
                      onClick={() => handleEditForm(selectedAudit)}
                      style={{
                        background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
                        color: 'white',
                        border: 'none',
                        padding: '8px 16px',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '0.875rem',
                        fontWeight: '500'
                      }}
                    >
                      ✏️ Editar Planilla
                    </button>
                  </div>
                </div>
                <div style={{ 
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '1rem',
                  fontSize: '0.875rem'
                }}>
                  <div>
                    <strong>Operador:</strong> {selectedAudit.operadorNombre}
                  </div>
                  <div>
                    <strong>Tiempo trabajado:</strong> {formatTime(selectedAudit.tiempoTrabajado)}
                  </div>
                  <div>
                    <strong>Eventos registrados:</strong> {selectedAudit.eventos?.length || 0}
                  </div>
                  <div>
                    <strong>Finalizada:</strong> {formatDate(selectedAudit.fechaTerminacion)}
                  </div>
                </div>

                {selectedAudit.observacionesOperador && (
                  <div style={{ 
                    marginTop: '15px',
                    padding: '15px',
                    backgroundColor: '#f9fafb',
                    borderRadius: '8px',
                    border: '1px solid #e5e7eb'
                  }}>
                    <strong style={{ display: 'block', marginBottom: '0.5rem' }}>
                      Observaciones del Operador:
                    </strong>
                    <p style={{ margin: 0, fontSize: '0.875rem', lineHeight: 1.5 }}>
                      {selectedAudit.observacionesOperador}
                    </p>
                  </div>
                )}
              </div>

              <div style={{ marginBottom: '25px' }}>
                <label style={{
                  display: 'block',
                  fontWeight: '600',
                  marginBottom: '0.5rem',
                  fontSize: '0.875rem'
                }}>
                  📝 Título del Informe (para PDF):
                </label>
                <input
                  type="text"
                  value={customTitle}
                  onChange={(e) => setCustomTitle(e.target.value)}
                  placeholder="Ingrese el título que aparecerá en el PDF"
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '0.875rem',
                    fontFamily: 'inherit'
                  }}
                />
              </div>

              <div style={{ marginBottom: '25px' }}>
                <label style={{
                  display: 'block',
                  fontWeight: '600', 
                  marginBottom: '8px',
                  color: '#374151'
                }}>
                  Comentarios del Supervisor:
                  <span style={{ color: '#dc3545', marginLeft: '4px' }}>*</span>
                  <small style={{ fontWeight: 'normal', marginLeft: '8px', color: '#6b7280' }}>
                    (Obligatorio para solicitar corrección)
                  </small>
                </label>
                <textarea
                  value={comentariosSupervisor}
                  onChange={(e) => setComentariosSupervisor(e.target.value)}
                  placeholder="Ejemplo: Las fotos están borrosas, necesito que tomes nuevas fotos más nítidas. También falta información sobre el sector norte del local."
                  rows={4}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    resize: 'vertical',
                    fontFamily: 'inherit',
                    fontSize: '0.9rem',
                    lineHeight: 1.5,
                    boxSizing: 'border-box'
                  }}
                  spellCheck={true}
                  lang="es"
                />
              </div>

              <div style={{ marginBottom: '25px' }}>
                <div style={{
                  padding: '20px',
                  backgroundColor: '#f0f9ff',
                  border: '1px solid #0ea5e9',
                  borderRadius: '8px'
                }}>
                  <h4 style={{ margin: '0 0 10px 0', color: '#0369a1', fontSize: '1rem' }}>
                    💡 Nuevo Flujo de Supervisión
                  </h4>
                  <p style={{ 
                    margin: '0', 
                    fontSize: '0.875rem', 
                    color: '#0369a1',
                    lineHeight: '1.5'
                  }}>
                    • <strong>Ver Reporte HTML:</strong> Revisar el contenido de la auditoría<br/>
                    • <strong>Editar Planilla:</strong> Modificar exactamente la misma planilla del operador<br/>
                    • <strong>Generar PDF:</strong> Crear documento final no editable para el cliente
                  </p>
                </div>
              </div>

              <div style={{ 
                display: 'flex', 
                gap: '12px', 
                justifyContent: 'flex-end'
              }}>
                <button
                  onClick={() => setShowReviewModal(false)}
                  className="btn btn-secondary"
                  disabled={processingApproval}
                  style={{ fontSize: '0.9rem' }}
                >
                  Cancelar
                </button>
                <button
                  onClick={() => handleApproveAudit(false)}
                  disabled={processingApproval}
                  style={{
                    background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                    color: 'white',
                    border: 'none',
                    padding: '12px 20px',
                    borderRadius: '8px',
                    cursor: processingApproval ? 'not-allowed' : 'pointer',
                    fontWeight: '600',
                    fontSize: '0.9rem',
                    opacity: processingApproval ? 0.6 : 1
                  }}
                >
                  {processingApproval ? '⏳' : '🔄'} Solicitar Corrección
                </button>
                <button
                  onClick={() => handleApproveAudit(true)}
                  disabled={processingApproval}
                  style={{
                    background: 'linear-gradient(135deg, #10b981, #059669)',
                    color: 'white',
                    border: 'none',
                    padding: '12px 20px',
                    borderRadius: '8px',
                    cursor: processingApproval ? 'not-allowed' : 'pointer',
                    fontWeight: '600',
                    fontSize: '0.9rem',
                    opacity: processingApproval ? 0.6 : 1
                  }}
                >
                  {processingApproval ? '⏳' : '✅'} Aceptar Auditoría
                </button>
                {selectedAudit?.estado === 'aprobada' && (
                  <button
                    onClick={async () => {
                      try {
                        setProcessingApproval(true);
                        const response = await auditsApi.generatePdfForClient(selectedAudit.id, customTitle);
                        if (response.data.success) {
                          alert(`PDF generado exitosamente: ${response.data.pdfFileName}`);
                          await loadAuditsForReview(); // Recargar lista
                        }
                      } catch (error) {
                        console.error('Error generando PDF:', error);
                        alert('Error al generar PDF para cliente');
                      } finally {
                        setProcessingApproval(false);
                      }
                    }}
                    disabled={processingApproval}
                    style={{
                      background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
                      color: 'white',
                      border: 'none',
                      padding: '12px 20px',
                      borderRadius: '8px',
                      cursor: processingApproval ? 'not-allowed' : 'pointer',
                      fontWeight: '600',
                      fontSize: '0.9rem',
                      opacity: processingApproval ? 0.6 : 1
                    }}
                  >
                    {processingApproval ? '⏳' : '📄'} Generar PDF Final
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupervisorAuditReview;